import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { LibraryModule } from '../../app/shared/lib/lib.module';
export interface Participant {
  id: string;
  name: string;
  identifier: string;
  /** Date of birth from delivery queue (ISO / DateOnly string). */
  dob?: string | null;
  /** Transportation for transport delivery screen (To Center / To Home / Round Trip). */
  transportType?: string | null;
  status:
    | 'yet-to-check-in'
    | 'check-in'
    | 'delivered'
    | 'waiting-list'
    | 'not-delivered'
    | 'no-show'
    | 'canceled';
  waitingListCount?: number;
}

@Component({
  selector: 'app-participant-card',
  standalone: true,
  imports: [CommonModule, LibraryModule],
  templateUrl: './participant-card.component.html',
  styleUrls: ['./participant-card.component.css']
})
export class ParticipantCardComponent {
  @Input() participant!: Participant;
  @Input() isSelected: boolean = false;
  @Output() cardClick = new EventEmitter<Participant>();

  getInitials(name: string): string {
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  }

  /** Format DOB for staff delivery list (MM/DD/YYYY). */
  formatDob(dob?: string | null): string {
    if (!dob) {
      return '';
    }
    const raw = String(dob).trim();
    if (!raw) {
      return '';
    }
    // Already MM/DD/YYYY
    if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(raw)) {
      return raw;
    }
    // yyyy-mm-dd (DateOnly from API) — avoid timezone shift
    const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (iso) {
      return `${iso[2]}/${iso[3]}/${iso[1]}`;
    }
    const parsed = new Date(raw);
    if (Number.isNaN(parsed.getTime())) {
      return raw;
    }
    const mm = String(parsed.getMonth() + 1).padStart(2, '0');
    const dd = String(parsed.getDate()).padStart(2, '0');
    const yyyy = parsed.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  }

  get secondaryLine(): string {
    const transportType = (this.participant.transportType || '').trim();
    if (transportType) {
      return transportType;
    }
    const dob = this.formatDob(this.participant.dob);
    if (dob) {
      return dob;
    }
    return this.participant.identifier ? this.participant.identifier : '-';
  }
  
  getStatusBorderColor(): string {
    switch (this.participant.status) {
      case 'yet-to-check-in':
        return '#42b0d5';
      case 'check-in':
        return '#91C3F8';
      case 'delivered':
        return '#40AB35';
      case 'waiting-list':
        return '#F26F24';
      case 'not-delivered':
        return '#B42318';
      case 'no-show':
        return '#7A5AF8';
      case 'canceled':
        return '#667085';
      default:
        return '#D0D1D4';
    }
  }

  onCardClick(): void {
    this.cardClick.emit(this.participant);
  }

  handleCardKeyDown(event: KeyboardEvent): void {
    // Handle Enter or Space key to trigger card click (accessibility)
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.onCardClick();
    }
  }
}

